/**
 * AdminExceptionDrawerHost - opens the shared ``ExceptionDrawer`` for a
 * cross-FP ``ExceptionActivityItem`` from the DQ Workbench (admin) side.
 *
 * The inspector mounts ``ExceptionDrawer`` from a table row; the admin
 * surfaces (Overview activity feed, Notes tab, Trending drill-down) have
 * an activity item instead. This host is the single place that maps an
 * activity item onto the drawer's props:
 *   - the tuple (``exception_code`` / ``data_domain`` / ``object_id`` +
 *     ``fp_code``) the drawer needs to load notes + status history,
 *   - ``fpContext`` so the floating scope pill names the franchise,
 *   - ``initialNotesExpanded`` so the user lands on the thread (every
 *     admin entry point here is note-centric).
 *
 * ``franchiseData`` is intentionally omitted - the "Sync latest data"
 * affordance is an inspector concern, not an admin one.
 */
import React, { useCallback, useState } from 'react'

import ExceptionDrawer from '../../../../components/inspector/shared/ExceptionDrawer'
import type {
  ActivityStatus,
  ExceptionActivityItem,
} from '../../../../services/exceptionActivityApi'
import { useExceptionDrawerDetail } from '../../../../hooks/useExceptionDrawerDetail'
import { getExceptionConfig } from '../../../../config/exceptions/registry'
import {
  dataDomainToObjectType,
  generateExceptionUrl,
} from '../../../../utils/urls'

/**
 * Map the admin activity status to the inspector ``?status=`` lifecycle
 * slice for the report deep-link (``buildReportHref``), so the linked
 * report opens on the filter-strip slice that shows this row.
 * ``needs_review`` is an open row, so it maps to the ``open`` slice;
 * blocked / resolved / ignored map 1:1.
 */
const STATUS_SLICE: Record<ActivityStatus, string> = {
  needs_review: 'open',
  blocked: 'blocked',
  resolved: 'resolved',
  ignored: 'ignored',
}

/**
 * Build the inspector exception-report deep-link for an activity item:
 * the FP's report for this exception code with the same row's drawer
 * re-opened (``?exceptionObject=`` + the matching lifecycle ``?status=``).
 * Returns undefined when the data domain has no inspector route segment.
 */
function buildReportHref(item: ExceptionActivityItem): string | undefined {
  const objectType = dataDomainToObjectType(item.data_domain)
  if (!objectType) return undefined
  const base = generateExceptionUrl(item.fp_code, objectType, item.exception_code, item.fp_id)
  const params = new URLSearchParams({
    exceptionObject: item.object_id,
    status: STATUS_SLICE[item.status],
  })
  return `${base}?${params.toString()}`
}

export interface AdminExceptionDrawerControl {
  /** The activity item whose drawer is open, or null when closed. */
  selected: ExceptionActivityItem | null
  open: (item: ExceptionActivityItem) => void
  close: () => void
}

/** State for one admin drawer instance. Mirrors ``useExceptionDrawer``
 * (the table-row equivalent) but keys on the activity item. */
export function useAdminExceptionDrawer(): AdminExceptionDrawerControl {
  const [selected, setSelected] = useState<ExceptionActivityItem | null>(null)
  const open = useCallback((item: ExceptionActivityItem) => setSelected(item), [])
  const close = useCallback(() => setSelected(null), [])
  return { selected, open, close }
}

interface Props {
  selected: ExceptionActivityItem | null
  onClose: () => void
  /** Fired once on close if any lifecycle/note mutation happened, so the
   * host surface can refetch its counts (e.g. DataExceptionsContext.refresh). */
  onAfterMutation?: () => void
}

const AdminExceptionDrawerHost: React.FC<Props> = ({
  selected,
  onClose,
  onAfterMutation,
}) => {
  // One read-only call resolves the full drawer payload for this tuple
  // (fp_code + data_domain + exception_code + object_id): the same row +
  // status + linked siblings + Why/How the inspector list feeds the drawer,
  // so the admin drawer can't drift from it. ``loading`` drives the drawer's
  // existing skeleton; the central endpoint resolves the lifecycle slice
  // server-side (no ``status`` to pass). Title/scope pill/type name still pass
  // explicitly so the header is seeded during the fetch.
  const tuple = selected
    ? {
        data_domain: selected.data_domain,
        exception_code: selected.exception_code,
        object_id: selected.object_id,
      }
    : null

  const { row, exceptionType, allowIgnore, loading } = useExceptionDrawerDetail(
    selected?.fp_code,
    tuple,
    selected !== null,
  )

  return (
    <ExceptionDrawer
      open={selected !== null}
      onClose={onClose}
      row={row}
      loading={loading}
      fpCode={selected?.fp_code ?? ''}
      // The admin DQ drawer launches compare, so it owns the ?compareSale
      // deep-link on this surface.
      compareDeepLink
      exceptionType={exceptionType ?? undefined}
      allowIgnore={allowIgnore}
      getRowSubtitle={
        selected
          ? getExceptionConfig(selected.exception_code)?.getRowSubtitle
          : undefined
      }
      exceptionTypeName={
        selected?.exception_name_short ?? selected?.exception_name ?? undefined
      }
      fpContext={
        selected
          ? { franchise_name: selected.franchise_name, country: selected.country }
          : null
      }
      reportHref={selected ? buildReportHref(selected) : undefined}
      getRowTitle={() => selected?.reference ?? ''}
      initialNotesExpanded
      onAfterMutation={onAfterMutation}
    />
  )
}

export default AdminExceptionDrawerHost
